import CurrentLocation from "features/Onboarding/Screens/CurrentLocation"
import CurrentlyTeachingInSchool from "features/Onboarding/Screens/CurrentlyTeachingInSchool"
import CurriculumInformation from "features/Onboarding/Screens/CurriculumInformation"
import ExperienceInTeaching from "features/Onboarding/Screens/ExperienceInTeaching"
import GradesTheyTeach from "features/Onboarding/Screens/GradesTheyTeach"
import PhoneNumber from "features/Onboarding/Screens/PhoneNumber"
import ProfilePicture from "features/Onboarding/Screens/ProfilePicture"
import SubjectsTheyTeach from "features/Onboarding/Screens/SubjectsTheyTeach/index"
import TeachingLevel from "features/Onboarding/Screens/TeachingLevel"
import YearsOfExperience from "features/Onboarding/Screens/YearsOfExperience"
import { AfterSignUpData } from "features/Onboarding/types"
import { trackingService } from "utils/tracking"

enum OnboardingStepId {
  CURRENTLY_TEACHING_IN_SCHOOL = "currentlyTeachingInSchool",
  YEARS_OF_EXPERIENCE = "yearsOfExperience",
  PHONE_NUMBER = "phoneNumber",
  SUBJECTS_THEY_TEACH = "subjectsTheyTeach",
  GRADES_THEY_TEACH = "gradesTheyTeach",
  CURRICULUM_INFORMATION = "curriculumInformation",
  TEACHING_LEVEL = "teachingLevel",
  EXPERIENCE_IN_TEACHING = "experienceInTeaching",
  CURRENT_LOCATION = "currentLocation",
  DATE_OF_BIRTH = "dateOfBirth",
  PROFILE_PICTURE = "profilePicture",
}

export interface OnboardingStep {
  id: OnboardingStepId
  component: React.ComponentType<any>
}

interface CpatOnboardingFlowManagerProps {
  data: AfterSignUpData
  source?: string | null
  isB2B: boolean
}

function OnboardingStep(
  id: OnboardingStepId,
  Component: React.ComponentType<any>
): OnboardingStep {
  return { id, component: Component }
}

export class CpatQuestionnaireOnboardingFlowManager {
  data: AfterSignUpData
  source?: string | null
  isB2B: boolean

  constructor(props: CpatOnboardingFlowManagerProps) {
    this.data = props.data
    this.source = props.source || trackingService.getSignupSource()
    this.isB2B = props.isB2B
  }

  getFlow(): OnboardingStep[] {
    if (this.isB2B) {
      return this.getFlowForB2B()
    }
    return this.getFlowForB2C()
  }

  getFlowForB2B(): OnboardingStep[] {
    const flow: OnboardingStep[] = []

    flow.push(
      OnboardingStep(OnboardingStepId.SUBJECTS_THEY_TEACH, SubjectsTheyTeach),
      OnboardingStep(
        OnboardingStepId.CURRICULUM_INFORMATION,
        CurriculumInformation
      ),
      OnboardingStep(OnboardingStepId.TEACHING_LEVEL, TeachingLevel),
      OnboardingStep(OnboardingStepId.GRADES_THEY_TEACH, GradesTheyTeach)
    )

    return flow
  }

  getFlowForB2C(): OnboardingStep[] {
    const flow: OnboardingStep[] = []

    flow.push(
      OnboardingStep(
        OnboardingStepId.CURRENTLY_TEACHING_IN_SCHOOL,
        CurrentlyTeachingInSchool
      )
    )

    if (this.data.isTeachingInSchool) {
      flow.push(
        OnboardingStep(OnboardingStepId.YEARS_OF_EXPERIENCE, YearsOfExperience),
        OnboardingStep(OnboardingStepId.SUBJECTS_THEY_TEACH, SubjectsTheyTeach),
        OnboardingStep(
          OnboardingStepId.CURRICULUM_INFORMATION,
          CurriculumInformation
        ),
        OnboardingStep(OnboardingStepId.GRADES_THEY_TEACH, GradesTheyTeach)
      )
    } else {
      flow.push(
        OnboardingStep(
          OnboardingStepId.EXPERIENCE_IN_TEACHING,
          ExperienceInTeaching
        )
      )

      if (this.data.hasExperienceInTeaching) {
        flow.push(
          OnboardingStep(
            OnboardingStepId.YEARS_OF_EXPERIENCE,
            YearsOfExperience
          ),
          OnboardingStep(
            OnboardingStepId.SUBJECTS_THEY_TEACH,
            SubjectsTheyTeach
          ),
          OnboardingStep(
            OnboardingStepId.CURRICULUM_INFORMATION,
            CurriculumInformation
          ),
          OnboardingStep(OnboardingStepId.GRADES_THEY_TEACH, GradesTheyTeach)
        )
      } else {
        flow.push(
          OnboardingStep(
            OnboardingStepId.SUBJECTS_THEY_TEACH,
            SubjectsTheyTeach
          ),
          OnboardingStep(OnboardingStepId.GRADES_THEY_TEACH, GradesTheyTeach)
        )
      }
    }

    flow.push(
      OnboardingStep(OnboardingStepId.PHONE_NUMBER, PhoneNumber),
      OnboardingStep(OnboardingStepId.CURRENT_LOCATION, CurrentLocation),
      OnboardingStep(OnboardingStepId.PROFILE_PICTURE, ProfilePicture)
    )

    return flow
  }

  getFirstIncompleteStep(flow: OnboardingStep[]): number {
    for (let i = 0; i < flow.length; i++) {
      switch (flow[i].id) {
        case OnboardingStepId.CURRENTLY_TEACHING_IN_SCHOOL:
          if (this.data.isTeachingInSchool === null) return i
          break
        case OnboardingStepId.YEARS_OF_EXPERIENCE:
          if (this.data.yearsOfExperience === null) return i
          break
        case OnboardingStepId.SUBJECTS_THEY_TEACH:
          if (this.data.subjects.length === 0) return i
          break
        case OnboardingStepId.GRADES_THEY_TEACH:
          if (
            this.data.gradesTaught.length === 0 &&
            this.data.gradeInterests.length === 0
          )
            return i
          break
        case OnboardingStepId.CURRICULUM_INFORMATION:
          if (this.data.curricula.length === 0) return i
          break
        case OnboardingStepId.TEACHING_LEVEL:
          if (this.data.teachingLevels.length === 0) return i
          break
        case OnboardingStepId.EXPERIENCE_IN_TEACHING:
          if (this.data.hasExperienceInTeaching === null) return i
          break
        case OnboardingStepId.CURRENT_LOCATION:
          if (
            this.data.currentLocation.city === null &&
            this.data.currentLocation.state === null &&
            this.data.currentLocation.country === null
          )
            return i
          break
        case OnboardingStepId.DATE_OF_BIRTH:
          if (this.data.dateOfBirth === null) return i
          break
        case OnboardingStepId.PROFILE_PICTURE:
          if (this.data.profilePicture === null) return i
          break
      }
    }

    return flow.length - 1
  }
}
